import { create } from "zustand"

import type { Feature, GPTModelItem } from "@/api/models"
import type { SessionType } from "@/types"

import { SettingsService } from "@/services/settings-service"

type AvailableSettings = {
  availableGptModels: GPTModelItem[]
  availableFeatures: Feature[]
}

type Settings = {
  retrieveCount: number
  temperature: number
  enableContextualActions: boolean
}

export type AISettingsStore = {
  settings: Settings
  availableSettings: AvailableSettings
  changeSettings: (settings: Partial<Settings>) => void
  initSettings: (sessionType: SessionType) => void
  resetSettings: (sessionType: SessionType) => void
}

const defaultAvailableSettings: AvailableSettings = {
  availableGptModels: [],
  availableFeatures: [],
}

const defaultSettings: Settings = {
  retrieveCount: 20,
  temperature: 0.7,
  enableContextualActions: true,
}

export const useAISettingsStore = create<AISettingsStore>()((set, get) => ({
  settings: defaultSettings,
  availableSettings: defaultAvailableSettings,
  changeSettings(settings) {
    set({ settings: { ...get().settings, ...settings } })
  },
  async resetSettings(sessionType) {
    const settingsService = SettingsService.getInstance()
    await settingsService.resetSettingsToDefault()
    const settings = await settingsService.getActiveSettings()
    await settingsService.getActiveModel(sessionType)

    set({
      settings: {
        retrieveCount: settings.chatRagRetrievalCount,
        temperature: settings.chatTemperature,
        enableContextualActions: true,
      },
    })
  },
  async initSettings(sessionType) {
    const settingsService = SettingsService.getInstance()
    // Wait for SettingsService to finish its initialization if it's still in progress
    if (!settingsService.initialized) {
      await new Promise<void>((resolve) => {
        const handler = () => {
          SettingsService.initializedEvent.off("initialized", handler)
          resolve()
        }
        SettingsService.initializedEvent.on("initialized", handler)
      })
    }

    const { allowedModules } = await settingsService.getActiveModel(sessionType)

    const settingsToChange: Partial<AISettingsStore> = {
      availableSettings: {
        availableGptModels: allowedModules,
        availableFeatures: settingsService.settings.features || [],
      },
    }

    // Model selection is now handled by useGPTModelSettings and DB
    // No need to initialize gptModel, shouldStream, or suggestFollowupQuestions here

    set(settingsToChange)
  },
}))
